import { mulberry32 } from './math/prng.js';
export const TEXTURE_GEN_DEFAULTS = {
    radial: { kind: 'radial', hardness: 0.3 },
    star: { kind: 'star', points: 4, innerRatio: 0.12 },
    ring: { kind: 'ring', inner: 0.55, outer: 0.8, softness: 0.12 },
    streak: { kind: 'streak', falloff: 0.7, thickness: 0.18 },
    noise: { kind: 'noise', noise: 'value', cells: 8, seed: 1, invert: false },
};
/** Seamless lattice value noise: lattice coordinates wrap at `cells`, so the tile edges match. */
function wrappedValueNoise(u, v, cells, hash) {
    const x = u * cells;
    const y = v * cells;
    const xi = Math.floor(x);
    const yi = Math.floor(y);
    const fx = x - xi;
    const fy = y - yi;
    const sx = fx * fx * (3 - 2 * fx);
    const sy = fy * fy * (3 - 2 * fy);
    const w = (dx, dy) => hash(((xi + dx) % cells + cells) % cells, ((yi + dy) % cells + cells) % cells);
    const a = w(0, 0) + (w(1, 0) - w(0, 0)) * sx;
    const b = w(0, 1) + (w(1, 1) - w(0, 1)) * sx;
    return a + (b - a) * sy;
}
/** Seamless worley (cellular) noise: nearest feature point over wrapped cells. */
function wrappedWorley(u, v, cells, feature) {
    const x = u * cells;
    const y = v * cells;
    const xi = Math.floor(x);
    const yi = Math.floor(y);
    let min = Infinity;
    for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
            const cx = xi + dx;
            const cy = yi + dy;
            const [fx, fy] = feature(((cx % cells) + cells) % cells, ((cy % cells) + cells) % cells);
            const d = Math.hypot(cx + fx - x, cy + fy - y);
            if (d < min)
                min = d;
        }
    }
    return Math.min(1, min);
}
/**
 * Render a spec to a canvas. White RGB + shaped alpha (except noise, which
 * writes grayscale RGB at full alpha so it can be used as a mask/dissolve map).
 */
export function generateTextureCanvas(spec, size = 128) {
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    const img = ctx.createImageData(size, size);
    const d = img.data;
    const noiseAt = spec.kind === 'noise' ? makeNoiseSampler(spec) : null;
    for (let y = 0; y < size; y++) {
        for (let x = 0; x < size; x++) {
            const u = x / (size - 1);
            const v = y / (size - 1);
            const nx = u * 2 - 1;
            const ny = v * 2 - 1;
            let alpha = 0;
            let gray = 255;
            switch (spec.kind) {
                case 'radial': {
                    const r = Math.hypot(nx, ny);
                    alpha = 1 - smoothstepN(Math.min(spec.hardness, 0.99), 1, r);
                    break;
                }
                case 'star': {
                    const r = Math.hypot(nx, ny);
                    const a = Math.atan2(ny, nx);
                    // spike profile: radius limit oscillates between innerRatio and 1
                    const lobe = Math.abs(Math.cos((a * spec.points) / 2));
                    const limit = spec.innerRatio + (1 - spec.innerRatio) * Math.pow(lobe, 6);
                    alpha = 1 - smoothstepN(limit - 0.08, limit, r);
                    break;
                }
                case 'ring': {
                    const r = Math.hypot(nx, ny);
                    alpha =
                        smoothstepN(spec.inner - spec.softness, spec.inner, r) *
                            (1 - smoothstepN(spec.outer, spec.outer + spec.softness, r));
                    break;
                }
                case 'streak': {
                    const ax = Math.max(0, 1 - Math.abs(nx));
                    const ay = Math.exp(-(ny * ny) / (2 * spec.thickness * spec.thickness));
                    alpha = Math.pow(ax, spec.falloff * 2) * ay;
                    break;
                }
                case 'noise': {
                    // value in BOTH rgb and alpha: rgb feeds dissolve masks (.x), alpha
                    // lets graph multiplies carve blobby silhouettes (e.g. ink stains)
                    const n = noiseAt(u, v);
                    gray = Math.round(n * 255);
                    alpha = n;
                    break;
                }
            }
            const i = (y * size + x) * 4;
            d[i] = gray;
            d[i + 1] = gray;
            d[i + 2] = gray;
            d[i + 3] = Math.round(Math.min(1, Math.max(0, alpha)) * 255);
        }
    }
    ctx.putImageData(img, 0, 0);
    return canvas;
}
/** Deterministic multi-octave sampler with memoized lattice hashes. */
function makeNoiseSampler(spec) {
    const rng = mulberry32(spec.seed);
    const table = new Map();
    const points = new Map();
    const hash = (x, y) => {
        const k = `${x}_${y}`;
        let h = table.get(k);
        if (h === undefined) {
            h = rng();
            table.set(k, h);
        }
        return h;
    };
    const feature = (x, y) => {
        const k = `${x}_${y}`;
        let p = points.get(k);
        if (!p) {
            p = [rng(), rng()];
            points.set(k, p);
        }
        return p;
    };
    return (u, v) => {
        let n;
        if (spec.noise === 'value') {
            n =
                wrappedValueNoise(u, v, spec.cells, hash) * 0.6 +
                    wrappedValueNoise(u * 2, v * 2, spec.cells * 2, hash) * 0.3 +
                    wrappedValueNoise(u * 4, v * 4, spec.cells * 4, hash) * 0.1;
        }
        else {
            n = wrappedWorley(u, v, spec.cells, feature);
        }
        return spec.invert ? 1 - n : n;
    };
}
function smoothstepN(e0, e1, x) {
    const t = Math.min(1, Math.max(0, (x - e0) / Math.max(1e-6, e1 - e0)));
    return t * t * (3 - 2 * t);
}
export function generateTextureDataUri(spec, size = 128) {
    return generateTextureCanvas(spec, size).toDataURL('image/png');
}
/**
 * Procedural 4×4 flame-lick flipbook sheet: a white-hot teardrop that
 * flickers, licks upward, breaks into rising blobs and dies. White-on-alpha
 * like every builtin — hue comes from the layer's tint/gradient, so one sheet
 * serves fire, ghost-fire, plasma… Zero downloads and license-clean, which is
 * exactly what shipped mobile effects want.
 */
export function generateFireSheetCanvas(size = 512, frames = 16, cols = 4, seed = 3) {
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    const cell = size / cols;
    const rng = mulberry32(seed);
    // per-flame wobble phases, fixed across frames so the lick animates coherently
    const wob = Array.from({ length: 4 }, () => rng() * Math.PI * 2);
    const blob = (x, y, r, a) => {
        if (r <= 0.5 || a <= 0.003)
            return;
        const g = ctx.createRadialGradient(x, y, 0, x, y, r);
        g.addColorStop(0, `rgba(255,255,255,${Math.min(1, a)})`);
        g.addColorStop(0.55, `rgba(255,255,255,${Math.min(1, a * 0.55)})`);
        g.addColorStop(1, 'rgba(255,255,255,0)');
        ctx.fillStyle = g;
        ctx.fillRect(x - r, y - r, r * 2, r * 2);
    };
    for (let f = 0; f < frames; f++) {
        const t = f / (frames - 1);
        const cellX = (f % cols) * cell;
        const cellY = Math.floor(f / cols) * cell;
        // every blob must die out INSIDE the cell — flipbook sampling shows any
        // bleed as hard flat edges and neighbouring-frame ghosts
        const inset = cell * 0.03;
        const clampR = (x, y, r) => Math.min(r, x - (cellX + inset), cellX + cell - inset - x, y - (cellY + inset), cellY + cell - inset - y);
        ctx.save();
        ctx.beginPath();
        ctx.rect(cellX, cellY, cell, cell);
        ctx.clip();
        const cx = cellX + cell / 2;
        const bottom = cellY + cell * 0.82;
        const life = t < 0.5 ? 1 : Math.pow(1 - (t - 0.5) / 0.5, 1.5); // burn steady, then die fast
        const height = cell * (0.42 + 0.24 * Math.min(1, t * 2.2)); // licks upward fast
        const spineN = 9;
        for (let j = 0; j < spineN; j++) {
            const s = j / (spineN - 1); // 0 = base, 1 = tip
            // spine sways with height; faster near the tip
            const sway = Math.sin(wob[0] + t * 9 + s * 4.5) * 0.08 + Math.sin(wob[1] + t * 15 + s * 8) * 0.04;
            const x = cx + sway * cell * s;
            const y = bottom - s * height - (t > 0.55 ? (t - 0.55) * cell * 0.1 : 0); // dying flame lifts slightly
            const taper = (1 - s * 0.85) * (0.14 + 0.04 * Math.sin(wob[2] + t * 12 + s * 6));
            // near the tip the flame breaks into separated blobs
            const breakup = s > 0.55 ? 0.55 + 0.45 * Math.abs(Math.sin(wob[3] + t * 18 + j * 2.1)) : 1;
            blob(x, y, clampR(x, y, taper * cell * (0.8 + 0.4 * life)), life * breakup * (1 - s * 0.5));
        }
        // hot core at the base
        const coreY = bottom - height * 0.12;
        blob(cx, coreY, clampR(cx, coreY, cell * 0.13 * (0.7 + 0.3 * life)), life);
        ctx.restore();
    }
    return canvas;
}
/**
 * Procedural 4×4 smoke-puff flipbook sheet: blobs that grow, roughen and fade
 * across frames. Solves the flipbook cold-start (no assets needed).
 */
export function generateSmokeSheetCanvas(size = 512, frames = 16, cols = 4, seed = 7) {
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    const cell = size / cols;
    const rng = mulberry32(seed);
    const blobs = Array.from({ length: 7 }, () => ({
        a: rng() * Math.PI * 2,
        r: 0.12 + rng() * 0.22,
        size: 0.28 + rng() * 0.22,
        drift: 0.1 + rng() * 0.25,
    }));
    for (let f = 0; f < frames; f++) {
        const t = f / (frames - 1);
        const cellX = (f % cols) * cell;
        const cellY = Math.floor(f / cols) * cell;
        const cx = cellX + cell / 2;
        const cy = cellY + cell / 2;
        const grow = 0.45 + t * 0.55;
        const fade = 1 - t * t;
        // contain every puff inside its cell — bleed shows up as hard flat edges
        // and neighbouring-frame ghosts when the flipbook samples the sheet
        const inset = cell * 0.03;
        ctx.save();
        ctx.beginPath();
        ctx.rect(cellX, cellY, cell, cell);
        ctx.clip();
        for (const b of blobs) {
            const bx = cx + Math.cos(b.a) * (b.r + b.drift * t) * cell * grow * 0.62;
            const by = cy + Math.sin(b.a) * (b.r + b.drift * t) * cell * grow * 0.62 - t * cell * 0.08;
            const br = Math.min(b.size * cell * grow * 0.5, bx - (cellX + inset), cellX + cell - inset - bx, by - (cellY + inset), cellY + cell - inset - by);
            if (br <= 0.5)
                continue;
            const g = ctx.createRadialGradient(bx, by, 0, bx, by, br);
            g.addColorStop(0, `rgba(255,255,255,${0.5 * fade})`);
            g.addColorStop(0.65, `rgba(255,255,255,${0.28 * fade})`);
            g.addColorStop(1, 'rgba(255,255,255,0)');
            ctx.fillStyle = g;
            ctx.fillRect(bx - br, by - br, br * 2, br * 2);
        }
        ctx.restore();
    }
    return canvas;
}
